var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Cartesian2, Cartesian3, ConstantPositionProperty, ScreenSpaceEventType, } from "cesium";
import { Activity, ActivityLogger } from "../../utilities/activity-logger";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
function isEditable() {
    const retVal = (_target, key, descriptor) => {
        const original = descriptor.value;
        descriptor.value = function (...args) {
            const drawing2DImageHandler = this;
            if (drawing2DImageHandler.imageUnderEdit) {
                return original.apply(this, args);
            }
            else {
                console.error(`Call start on drawing2DImageHandler before calling ${String(key)}`);
            }
        };
        return descriptor;
    };
    return retVal;
}
export class Drawing2D2PointAlignmentHandler {
    _callback;
    _cesiumPicker;
    _imageUnderEdit;
    _cesiumViewer;
    _site;
    _handler;
    _onKeyDownFuncHandler;
    _previousLeftDownAction;
    _preview;
    _pointColor;
    _sourceEntities;
    _destinationEntities;
    get imageUnderEdit() {
        return this._imageUnderEdit;
    }
    constructor(args, cesiumViewer, site) {
        this._cesiumPicker = args.cesiumPicker;
        this._handler = args.handler;
        this._cesiumViewer = cesiumViewer;
        this._site = site;
        this._preview = args.preview;
        this._sourceEntities = [];
        this._destinationEntities = [];
    }
    async start(image, callback) {
        this._imageUnderEdit = image;
        this._callback = callback;
        this._previousLeftDownAction = this._handler.getInputAction(ScreenSpaceEventType.LEFT_DOWN);
        this._onKeyDownFuncHandler = this.onHandleKeyDown.bind(this);
        document.addEventListener("keydown", this._onKeyDownFuncHandler);
        this._callback?.onStart && this._callback?.onStart("Edit Started");
    }
    async end(args) {
        try {
            if (args?.twoPointArgs) {
                const { source1, source2, destination1, destination2 } = args.twoPointArgs;
                await this._imageUnderEdit.alignToTwoPoints({
                    source1: new Cartesian2(source1.x, source1.y),
                    source2: new Cartesian2(source2.x, source2.y),
                    destination1: new Cartesian2(destination1.x, destination1.y),
                    destination2: new Cartesian2(destination2.x, destination2.y),
                });
            }
            await this._imageUnderEdit.updateHeightMap();
            this.clearAll();
            ActivityLogger.log(Activity.REFERENCE_TOPOLOGY_EDITED);
        }
        catch (err) {
            this._callback?.onError && this._callback?.onError(err);
            return;
        }
        this._callback?.onFinish && this._callback?.onFinish("Edit Ended");
    }
    async cancel() {
        this.clearAll();
        this._callback?.onCancel && this._callback?.onCancel();
    }
    clearAll() {
        this._sourceEntities.forEach((entity) => {
            this._cesiumViewer.viewer.entities.remove(entity);
        });
        this._destinationEntities.forEach((entity) => {
            this._cesiumViewer.viewer.entities.remove(entity);
        });
        this._sourceEntities = [];
        this._destinationEntities = [];
        this._cesiumViewer.viewer.scene.screenSpaceCameraController.enableRotate = true;
        this._handler.removeInputAction(ScreenSpaceEventType.LEFT_DOWN);
        this._previousLeftDownAction &&
            this._handler.setInputAction(this._previousLeftDownAction, ScreenSpaceEventType.LEFT_DOWN);
        this._handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this._handler.removeInputAction(ScreenSpaceEventType.LEFT_UP);
        document.removeEventListener("keydown", this._onKeyDownFuncHandler);
        this._cesiumViewer.viewerCamera.set3DView();
        this._cesiumViewer.viewer.scene.requestRender();
        this._imageUnderEdit = undefined;
        this._preview.resetSphereColor();
        this._preview.setSphereVisibility(false);
    }
    onHoverOverImage(e) {
        const pickedPoint = this._cesiumPicker.getAllowPickedEntity(e.endPosition);
        if (pickedPoint?.type === "image" &&
            pickedPoint.id === this._imageUnderEdit.id) {
            const wgsPoint = this._cesiumPicker.getPointOnMousePos(e.endPosition);
            this._preview.setSpherePosition(wgsPoint);
        }
    }
    onHover(e) {
        const wgsPoint = this._cesiumPicker.getPointOnMousePos(e.endPosition);
        this._preview.setSpherePosition(wgsPoint);
    }
    async onHandleKeyDown(event) {
        switch (event.key) {
            case "Esc":
            case "Escape":
                await this.cancel();
                break;
            default:
        }
    }
    async pickSourcePoint(color) {
        return new Promise((resolve, _reject) => {
            this._preview.setSphereVisibility(true);
            this._preview.setSphereColor(color);
            this._handler.setInputAction(this.onHoverOverImage.bind(this), ScreenSpaceEventType.MOUSE_MOVE);
            this._handler.setInputAction(async ({ position: mousePosition }) => {
                const pickedObj = this._cesiumPicker.getAllowPickedEntity(mousePosition);
                if (pickedObj?.type === "image" &&
                    pickedObj.id === this._imageUnderEdit.id) {
                    const wgsPoint = this._cesiumViewer.viewer.scene.pickPosition(mousePosition);
                    const [localPoint] = await this._site.convertPointsToLocalSpace([
                        wgsPoint,
                    ]);
                    if (localPoint) {
                        this._handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
                        this._handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
                        this._preview.setSphereVisibility(false);
                        this._preview.resetSphereColor();
                        this._sourceEntities.push(EarthBrainUtils.addSphere(this._cesiumViewer.viewer, wgsPoint, color));
                        resolve({ position: localPoint, mousePosition: mousePosition });
                    }
                }
            }, ScreenSpaceEventType.LEFT_CLICK);
        });
    }
    async startPickingDestinationPoint(color, callback) {
        this._preview.setSphereVisibility(true);
        this._preview.setSphereColor(color);
        this._handler.setInputAction(this.onHover.bind(this), ScreenSpaceEventType.MOUSE_MOVE);
        this._handler.setInputAction(async ({ position: mousePosition }) => {
            const wgsPoint = this._cesiumPicker.getPointOnMousePos(mousePosition);
            if (wgsPoint) {
                const [localPoint] = await this._site.convertPointsToLocalSpace([
                    wgsPoint,
                ]);
                if (localPoint) {
                    if (this._destinationEntities.length > 0 &&
                        this._destinationEntities.length >= this._sourceEntities.length) {
                        this._destinationEntities[this._destinationEntities.length - 1].position = new ConstantPositionProperty(wgsPoint);
                    }
                    else {
                        this._destinationEntities.push(EarthBrainUtils.addSphere(this._cesiumViewer.viewer, wgsPoint, color));
                    }
                    this._cesiumViewer.viewer.scene.requestRender();
                    callback({ position: localPoint, mousePosition: mousePosition });
                }
            }
        }, ScreenSpaceEventType.LEFT_CLICK);
    }
    async endPickingDestinationPoint() {
        this._handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
        this._handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this._preview.setSphereVisibility(false);
        this._preview.resetSphereColor();
    }
    /**
     * Updates the position of the destination entity (blue point) based on numeric input
     * @param localPosition Local space position from numeric input (x, y, z)
     */
    async updateDestinationPosition(localPosition) {
        // Cache entity reference before await
        const entity = this._destinationEntities[this._destinationEntities.length - 1];
        if (!entity) {
            return; // Early return if no destination entity exists
        }
        // Create local point with input X, Y, Z
        const newLocalPoint = new Cartesian3(localPosition.x, localPosition.y, localPosition.z);
        // Convert to WGS84
        const wgsPoints = await this._site.convertPointsToWGS84([newLocalPoint]);
        if (!wgsPoints || wgsPoints.length === 0 || !wgsPoints[0]) {
            console.warn("Failed to convert position to WGS84");
            return;
        }
        // Verify entity still exists in array after async operation
        if (!this._destinationEntities.includes(entity)) {
            return;
        }
        entity.position = new ConstantPositionProperty(wgsPoints[0]);
        this._cesiumViewer.viewer.scene.requestRender();
    }
}
__decorate([
    isEditable()
], Drawing2D2PointAlignmentHandler.prototype, "end", null);
__decorate([
    isEditable()
], Drawing2D2PointAlignmentHandler.prototype, "cancel", null);
//# sourceMappingURL=drawing-2d-2Point-Alignment-handler.js.map